import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  UsersRound, Plus, Search, Info, Pencil, Trash2,
  ArrowRight, HelpCircle,
  CalendarClock, UserPlus, Settings, PlayCircle,
} from "lucide-react";
import "../../../styles/payroll-setup.css";

function freqPill(freq) {
  return freq === "Weekly" ? "ps-pill-variable" : "ps-pill-fixed";
}

const GUIDE_STEPS = [
  {
    icon: Plus,
    title: "Create a Pay Group",
    desc: 'Click the "+ Add Pay Group" button above. Enter a name (e.g. Monthly Staff), a code, payroll frequency, and a description.',
  },
  {
    icon: UserPlus,
    title: "Assign Employees",
    desc: "Go to Employees → select an employee → Salary tab → choose the pay group. Each employee can belong to one pay group.",
    link: { label: "Go to Employees", path: "/employees?module=employees" },
  },
  {
    icon: CalendarClock,
    title: "Set Pay Cycle & Dates",
    desc: "The pay group's frequency (Monthly / Weekly) determines the payroll cycle. Processing dates are applied automatically during payroll runs.",
  },
  {
    icon: PlayCircle,
    title: "Run Payroll by Group",
    desc: "During payroll processing, select a pay group to process only that group's employees. This lets you run monthly and weekly payrolls separately.",
    link: { label: "Go to Payroll Processing", path: "/process-payroll" },
  },
  {
    icon: Settings,
    title: "Manage & Review",
    desc: "Edit or deactivate pay groups anytime. The employee count updates automatically. Inactive groups are excluded from payroll runs.",
  },
];

export default function PayGroupsTab() {
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [guideOpen, setGuideOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = groups;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (g) =>
          g.name.toLowerCase().includes(q)
          || g.code.toLowerCase().includes(q)
          || g.description.toLowerCase().includes(q)
      );
    }
    if (statusFilter === "active") list = list.filter((g) => g.active);
    else if (statusFilter === "inactive") list = list.filter((g) => !g.active);
    return list;
  }, [groups, search, statusFilter]);

  function addGroup() {
    const n = groups.length + 1;
    setGroups((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: `New Pay Group ${n}`,
        code: `GROUP_${n}`,
        description: "Describe who belongs in this pay group.",
        frequency: "Monthly",
        count: 0,
        active: true,
      },
    ]);
  }

  function removeGroup(id) {
    setGroups((prev) => prev.filter((g) => g.id !== id));
  }

  return (
    <div>
      <div className="ps-section-head">
        <div className="ps-section-title-row">
          <span className="ps-section-icon"><UsersRound size={22} strokeWidth={2} /></span>
          <div>
            <h2 className="ps-section-title">3.4 Pay Groups</h2>
            <p className="ps-section-desc">Group employees based on payroll cycle for salary processing.</p>
          </div>
        </div>
        <div className="ps-section-actions" style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className="bw-btn bw-btn-ghost"
            onClick={() => setGuideOpen((v) => !v)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <HelpCircle size={15} />
            {guideOpen ? "Hide Guide" : "How It Works"}
          </button>
          <button type="button" className="bw-btn bw-btn-primary" onClick={addGroup}>
            <Plus size={15} /> Add Pay Group
          </button>
        </div>
      </div>

      {guideOpen && (
        <div className="pg-guide">
          <div className="pg-guide-header">
            <HelpCircle size={18} />
            <div>
              <div className="pg-guide-title">How Pay Groups Work</div>
              <div className="pg-guide-sub">Follow these steps to set up and use pay groups for payroll processing.</div>
            </div>
          </div>
          <ol className="pg-guide-steps">
            {GUIDE_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <li key={i} className="pg-guide-step">
                  <div className="pg-guide-step-num">
                    <span>{i + 1}</span>
                  </div>
                  <div className="pg-guide-step-icon">
                    <Icon size={16} />
                  </div>
                  <div className="pg-guide-step-body">
                    <div className="pg-guide-step-title">{step.title}</div>
                    <div className="pg-guide-step-desc">{step.desc}</div>
                    {step.link ? (
                      <Link to={step.link.path} className="pg-guide-step-link">
                        {step.link.label} <ArrowRight size={12} />
                      </Link>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="ps-toolbar">
        <div className="ps-search">
          <Search size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pay groups…"
            aria-label="Search pay groups"
          />
        </div>
        <select className="ps-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter">
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="ps-info-banner">
        <Info size={16} />
        <span>Pay groups help you process payroll separately for different employee categories based on their payroll cycle or contract type.</span>
      </div>

      <div className="ps-table-block">
        <div className="ps-table-wrap">
          <table className="ps-table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Description</th>
                <th>Payroll Frequency</th>
                <th>Employee Count</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.id}>
                  <td>
                    <div className="ps-comp-name">{g.name}</div>
                    <div className="ps-comp-code">Code: {g.code}</div>
                  </td>
                  <td>{g.description}</td>
                  <td><span className={`ps-pill ${freqPill(g.frequency)}`}>{g.frequency}</span></td>
                  <td>{g.count}</td>
                  <td>
                    <span className={g.active ? "ps-pill-active" : "ps-pill-inactive"}>
                      {g.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="ps-row-actions">
                      <button type="button" className="ps-act" title="Edit" aria-label={`Edit ${g.name}`}>
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        className="ps-act ps-act-danger"
                        title="Delete"
                        aria-label={`Delete ${g.name}`}
                        onClick={() => removeGroup(g.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="ps-table-empty">
                    <div className="pg-empty">
                      <UsersRound size={28} strokeWidth={1.5} />
                      <div className="pg-empty-title">No pay groups yet</div>
                      <div className="pg-empty-desc">
                        Create your first pay group to organise employees by payroll cycle.
                      </div>
                      <div className="pg-empty-actions">
                        <button type="button" className="bw-btn bw-btn-primary bw-btn-sm" onClick={addGroup}>
                          <Plus size={14} /> Add Pay Group
                        </button>
                        <button
                          type="button"
                          className="bw-btn bw-btn-ghost bw-btn-sm"
                          onClick={() => setGuideOpen(true)}
                        >
                          <HelpCircle size={14} /> How It Works
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="ps-pagination">
            <span>Showing 1 to {filtered.length} of {filtered.length} entries</span>
          </div>
        )}
      </div>

      <div className="ps-footer-banner">
        <span><strong>Used In:</strong> Pay groups are used during payroll processing to apply the correct pay cycle and processing dates for employees.</span>
        <Link to="/process-payroll">Go to Payroll Processing &gt;</Link>
      </div>
    </div>
  );
}
